/* eslint-disable no-console */
import React, { useEffect, useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { FaMobileScreen } from "react-icons/fa6";
import { FaLocationDot } from "react-icons/fa6";
import { HiOutlineBuildingOffice } from "react-icons/hi2";
import { IoPeople } from "react-icons/io5";
import { MdElevator } from "react-icons/md";
import { PiClockUserFill } from "react-icons/pi";
import { TiStar } from "react-icons/ti";
import { Loader } from "..";
import { EDIFICIOS_EXCLUIDOS } from "../../constants/permisos";
import { useAuthContext } from "../../context/auth";
import { supabase } from "../../utils";
import { getLocale } from "../../utils/getLocale";
import PdfPortafolio from "../PdfPortafolio";
import { pdf } from "@react-pdf/renderer";
import "./stylesFichaEdificios.scss";

const compressImage = (src, maxWidth = 500, quality = 0.55) =>
  new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

export const FichaEdificios = () => {
  const [edificios, setEdificios] = useState([]);
  const [imageSrc, setImageSrc] = useState({});
  const [filtroUbicacion, setFiltroUbicacion] = useState([]);
  const [filtroEdificio, setFiltroEdificio] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);

  const { pais } = useAuthContext();

  useEffect(() => {
    const initialize = async () => {
      const data = await supabase.select("edificios", {
        filters: { pais },
        orderBy: "pantallas",
        ascending: false,
      });

      // Incluir edificios OPERATIVOS e INSTALACIÓN, excluir IDs 119 y 121
      const filteredData = data.filter(
        (e) => (e.estado === "OPERATIVO" || e.estado === "INSTALACIÓN") && !EDIFICIOS_EXCLUIDOS.includes(e.id)
      );

      // Ordenar por personas desc, luego por suma de dispositivos
      filteredData.sort((a, b) => {
        if (b.personas !== a.personas) return b.personas - a.personas;
        const sumaA = (a.pantallas || 0) + (a.totems || 0) + (a.cowork || 0) + (a.espera || 0);
        const sumaB = (b.pantallas || 0) + (b.totems || 0) + (b.cowork || 0) + (b.espera || 0);
        return sumaB - sumaA;
      });

      setEdificios(filteredData);
    };

    initialize();
  }, [pais]);

  useEffect(() => {
    if (edificios.length === 0) return;

    const loadImages = async () => {
      const results = await Promise.all(
        edificios.map(async (edificio) => {
          try {
            const src = await import(`../../img/edificios/${edificio.id}.png`).catch(
              () => import(`../../img/edificios/${edificio.id}.jpeg`)
            );
            return [edificio.id, src.default];
          } catch {
            return [edificio.id, null];
          }
        })
      );
      setImageSrc(Object.fromEntries(results));
    };

    loadImages();
  }, [edificios]);

  // Opciones únicas para filtros
  const ubicacionesUnicas = useMemo(
    () => [...new Set(edificios.map((e) => e.ubicacion))].filter(Boolean).sort(),
    [edificios]
  );

  // Excluir del dropdown de edificio los que ya están cubiertos por ubicación
  const edificiosUnicos = useMemo(() => {
    const filtered = edificios.filter((e) => filtroUbicacion.length === 0 || !filtroUbicacion.includes(e.ubicacion));
    return [...new Set(filtered.map((e) => e.edificio))].filter(Boolean).sort();
  }, [edificios, filtroUbicacion]);

  // Edificios filtrados (unión: ubicación OR edificio)
  const filteredEdificios = useMemo(() => {
    const hasUbicacion = filtroUbicacion.length > 0;
    const hasEdificio = filtroEdificio.length > 0;
    if (!hasUbicacion && !hasEdificio) return edificios;
    return edificios.filter(
      (e) =>
        (hasUbicacion && filtroUbicacion.includes(e.ubicacion)) || (hasEdificio && filtroEdificio.includes(e.edificio))
    );
  }, [edificios, filtroUbicacion, filtroEdificio]);

  const toggleFiltroUbicacion = (ubicacion) => {
    const newUbicaciones = filtroUbicacion.includes(ubicacion)
      ? filtroUbicacion.filter((u) => u !== ubicacion)
      : [...filtroUbicacion, ubicacion];
    setFiltroUbicacion(newUbicaciones);
    // Limpiar edificios que ahora quedan cubiertos por la ubicación seleccionada
    if (newUbicaciones.length > 0) {
      const edificiosCubiertos = edificios.filter((e) => newUbicaciones.includes(e.ubicacion)).map((e) => e.edificio);
      setFiltroEdificio((prev) => prev.filter((name) => !edificiosCubiertos.includes(name)));
    }
  };

  const toggleFiltroEdificio = (nombre) =>
    setFiltroEdificio((prev) => (prev.includes(nombre) ? prev.filter((e) => e !== nombre) : [...prev, nombre]));

  const renderTotemsText = (edificio) => {
    const cant = edificio.totems || edificio.cowork;
    if (!cant) return "-";
    return `${cant} ${cant > 1 ? "TÓTEMS" : "TÓTEM"} de circulación`;
  };

  const renderEsperaText = (edificio) => {
    if (!edificio.espera) return "-";
    return `${edificio.espera} ${edificio.espera > 1 ? "PANTALLAS" : "PANTALLA"} en hall de espera`;
  };

  const renderEmpresasInfo = (empresaslistado) => {
    if (!empresaslistado || empresaslistado.toUpperCase() === "NO DISPONIBLE") {
      return "PENDIENTE";
    }
    const cantidad = empresaslistado.split(",").length;
    return `${cantidad} EMPRESAS`;
  };

  const hasFilters = filtroUbicacion.length > 0 || filtroEdificio.length > 0;

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      // Comprimir imágenes para reducir tamaño del PDF
      const entries = await Promise.all(
        filteredEdificios.map(async (e) => {
          const original = imageSrc[e.id];
          if (!original) return [e.id, null];
          const compressed = await compressImage(original);
          return [e.id, compressed];
        })
      );
      const compressedImages = Object.fromEntries(entries);

      const doc = <PdfPortafolio edificios={filteredEdificios} pais={pais} imageSrc={compressedImages} />;
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Portafolio_${pais}_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setPdfLoading(false);
    }
  };

  if (edificios === undefined) return <Loader />;

  if (edificios.length === 0) {
    return <div>No hay edificios disponibles.</div>;
  }

  return (
    <div className="body-ficha">
      {/* Barra de filtros sticky */}
      <div className="portafolio-filter-bar">
        <div className="portafolio-filter-row">
          {/* Filtro Ubicación */}
          <Dropdown autoClose="outside">
            <Dropdown.Toggle
              variant="dark"
              className={`portafolio-filter-toggle ${filtroUbicacion.length > 0 ? "active-filter" : ""}`}
            >
              {filtroUbicacion.length > 0
                ? filtroUbicacion.length <= 2
                  ? filtroUbicacion.join(", ")
                  : `${filtroUbicacion.length} ubicaciones`
                : "Ubicación"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="portafolio-dropdown-menu">
              {ubicacionesUnicas.map((ubicacion, idx) => (
                <div key={idx} className="portafolio-filter-option" onClick={() => toggleFiltroUbicacion(ubicacion)}>
                  <input type="checkbox" checked={filtroUbicacion.includes(ubicacion)} readOnly />
                  <label>{ubicacion}</label>
                </div>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          {/* Filtro Edificio */}
          <Dropdown autoClose="outside">
            <Dropdown.Toggle
              variant="dark"
              className={`portafolio-filter-toggle ${filtroEdificio.length > 0 ? "active-filter" : ""}`}
            >
              {filtroEdificio.length > 0
                ? filtroEdificio.length <= 2
                  ? filtroEdificio.join(", ")
                  : `${filtroEdificio.length} edificios`
                : "Edificio"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="portafolio-dropdown-menu">
              {edificiosUnicos.map((nombre, idx) => (
                <div key={idx} className="portafolio-filter-option" onClick={() => toggleFiltroEdificio(nombre)}>
                  <input type="checkbox" checked={filtroEdificio.includes(nombre)} readOnly />
                  <label>{nombre}</label>
                </div>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          {/* Botón PDF */}
          <button className="portafolio-pdf-btn" disabled={pdfLoading} onClick={handleDownloadPdf}>
            {pdfLoading ? "Generando..." : "PDF"}
          </button>

          {/* Limpiar filtros */}
          {hasFilters && (
            <button
              className="portafolio-clear-btn"
              onClick={() => {
                setFiltroUbicacion([]);
                setFiltroEdificio([]);
              }}
            >
              Limpiar filtros
            </button>
          )}

          {/* Contador */}
          <span className="portafolio-filter-count">
            {filteredEdificios.length} edificio{filteredEdificios.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Fichas de edificios */}
      {filteredEdificios.map((edificio) => (
        <div key={edificio.id} className="ficha-edificios">
          {edificio.estado === "INSTALACIÓN" && <div className="instalacion-label">EN INSTALACIÓN</div>}
          {imageSrc[edificio.id] ? (
            <img className="edificios-imagen-edificio" src={imageSrc[edificio.id]} alt={edificio.edificio} />
          ) : (
            <div className="edificios-imagen-placeholder">foto pendiente :(</div>
          )}
          <div className="nombre-edificio">
            {edificio.edificio || ""}
            <span className="pantallas-total-mobile">
              {(edificio.pantallas || 0) + (edificio.totems || edificio.cowork || 0) + (edificio.espera || 0)} pantallas
            </span>
          </div>
          <div className="div-ubicacion">
            <FaLocationDot className="ubicacion-icono" />
            <div className="ubicacion-texto">
              {(edificio.direccion ?? "").toUpperCase()}, {edificio.ubicacion}
            </div>
          </div>
          <div className="div-ascensores">
            <MdElevator className="ascensores-icono" />
            <div className="ascensores-texto">
              {edificio.pantallas ? `${edificio.pantallas} ASCENSORES con pantallas FULLHD` : "-"}
            </div>
          </div>
          <div className="div-totems">
            <FaMobileScreen className="totems-icono" />
            <div className="totems-texto">{renderTotemsText(edificio)}</div>
          </div>
          <div className="div-espera">
            <PiClockUserFill className="espera-icono" />
            <div className="espera-texto">{renderEsperaText(edificio)}</div>
          </div>
          <div className="contenedor-blanco">
            <div className="div-people">
              <IoPeople className="people-icono" />
              <div className="people-numero">{(edificio.personas ?? 0).toLocaleString(getLocale(pais))}</div>
              <div className="people-texto">PERSONAS ÚNICAS DIARIAS</div>
            </div>
            <div className="div-empresas">
              <HiOutlineBuildingOffice className="empresas-icono" />
              <div className="empresas-texto">{renderEmpresasInfo(edificio.empresaslistado)}</div>
            </div>
            <div className="div-empresas-destacadas">
              <TiStar className="empresas-destacadas-icono" />
              <div className="empresas-destacadas-texto">{(edificio.empresasdestacadas || "").toUpperCase()}</div>
            </div>
            <div className="linea-vertical" />
          </div>
        </div>
      ))}
    </div>
  );
};
